import { AdminMembersReportPageParams } from "@/admin/insights/member-reports/AdminMembersReportPage"
import AdminMembersReportStandardProfileFieldsTableRow, {
  AdminMembersReportStandardProfileFieldsTableRowSkeleton,
} from "@/admin/insights/member-reports/tabs/overview/AdminMembersReportStandardProfileFieldsTableRow"
import { AdminMembersReportStandardProfileFieldsTableQuery } from "@/admin/insights/member-reports/tabs/overview/__generated__/AdminMembersReportStandardProfileFieldsTableQuery.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useLabels } from "@/core/context/LabelsContext"
import MemberGroupTag from "@/product/common/member-group/common/tag/MemberGroupTag"
import Relay from "@/relay/relayUtils"
import useUserTimezone from "@/user/util/useUserTimezone"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoSection,
  DiscoTable,
  DiscoTableSkeletonWithHeader,
  DiscoText,
} from "@disco-ui"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import { Table, TableBody, TableCell, TableHead } from "@material-ui/core"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { DATE_FORMAT } from "@utils/time/timeConstants"
import { formatInTimeZone } from "date-fns-tz"
import { graphql } from "relay-runtime"

const headers = [{ value: "Field Name" }, { value: "Response" }]

function AdminMembersReportsStandardProfileFieldsTable() {
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const activeOrganization = useActiveOrganization()!
  const labels = useLabels()
  const classes = useStyles()
  const authUserTimezone = useUserTimezone()
  const canReadPrivate = activeOrganization.viewerIsOwnerOrAdmin

  const [{ node }] =
    Relay.useRefetchableQuery<AdminMembersReportStandardProfileFieldsTableQuery>(
      graphql`
        query AdminMembersReportStandardProfileFieldsTableQuery(
          $id: ID!
          $canReadPrivate: Boolean!
          $inProductsViewerIsAdminOf: Boolean
        ) {
          node(id: $id) {
            __typename
            ... on OrganizationMembership {
              id
              email @include(if: $canReadPrivate)
              creationDatetime
              lastActivityAt
              member {
                fullName
                bio
                timezone
              }
              groupMemberships(inProductsViewerIsAdminOf: $inProductsViewerIsAdminOf) {
                edges {
                  node {
                    id
                    memberGroup {
                      id
                      ...MemberGroupTagFragment
                    }
                  }
                }
              }
              organization {
                defaultFields: profileFields(status: visible, isDefault: true) {
                  edges {
                    node {
                      id
                      title
                      isPrivate
                    }
                  }
                }
              }
            }
          }
        }
      `,
      {
        id: queryParams.om!,
        inProductsViewerIsAdminOf: !(
          activeOrganization.viewerIsOwnerOrAdmin ||
          activeOrganization.viewerMembership?.hasGroupLeadRole
        ),
        canReadPrivate,
      },
      { fetchPolicy: "network-only" }
    )

  const organizationMembership = Relay.narrowNodeType(node, "OrganizationMembership")
  if (!organizationMembership) return null

  const memberGroups = Relay.connectionToArray(
    organizationMembership.groupMemberships
  ).map((groupMembership) => groupMembership.memberGroup)

  const profileFields = Relay.connectionToArray(
    organizationMembership.organization.defaultFields
  ).filter((pf) => (canReadPrivate ? true : !pf.isPrivate))

  const showBio = profileFields.some((pf) => pf.title === "Short Bio")
  const showGroups = profileFields.some((pf) => pf.title === "Public Groups")
  const showTimezone = profileFields.some((pf) => pf.title === "Local Timezone")

  return (
    <DiscoSection padding={2.5} groovyDepths={"insideCard"} className={classes.section}>
      <DiscoText variant={"heading-xs-600"} marginBottom={1} marginLeft={1}>
        {"Standard Fields"}
      </DiscoText>
      <Table classes={{ root: classes.root }}>
        <TableHead className={classes.headerRow}>
          <DiscoTable.Row>
            {headers.map(({ value }) => (
              <TableCell key={value}>
                <DiscoText variant={"body-xs-600"} noWrap>
                  {value}
                </DiscoText>
              </TableCell>
            ))}
          </DiscoTable.Row>
        </TableHead>

        {/* Rows */}
        <TableBody data-testid={`AdminMembersReportStandardProfileFIeldsTable.body`}>
          {[
            <AdminMembersReportStandardProfileFieldsTableRow
              key={"member_name"}
              fieldName={`${labels.admin_member.singular} Name`}
              response={organizationMembership.member.fullName}
            />,
            ...ArrayUtils.spreadIf(
              <AdminMembersReportStandardProfileFieldsTableRow
                key={"bio"}
                fieldName={"Bio"}
                response={organizationMembership.member.bio}
              />,
              showBio
            ),
            ...ArrayUtils.spreadIf(
              <AdminMembersReportStandardProfileFieldsTableRow
                key={"email"}
                fieldName={"Email"}
                response={organizationMembership.email}
              />,
              canReadPrivate
            ),
            ...ArrayUtils.spreadIf(
              <AdminMembersReportStandardProfileFieldsTableRow
                key={"groups"}
                fieldName={"Groups"}
                response={
                  <span className={classes.memberGroupsContainer}>
                    {memberGroups.map((mg) => (
                      <MemberGroupTag key={mg.id} memberGroupKey={mg} />
                    ))}
                  </span>
                }
              />,
              showGroups
            ),
            ...ArrayUtils.spreadIf(
              <AdminMembersReportStandardProfileFieldsTableRow
                key={"timezone"}
                fieldName={"Timezone"}
                response={`${formatInTimeZone(
                  new Date(),
                  organizationMembership.member.timezone,
                  DATE_FORMAT.SHORT_TIME_WITH_ZONE
                )} local time`}
              />,
              showTimezone
            ),
            <AdminMembersReportStandardProfileFieldsTableRow
              key={"join_date"}
              fieldName={"Join date"}
              response={
                organizationMembership.creationDatetime
                  ? formatInTimeZone(
                      organizationMembership.creationDatetime,
                      authUserTimezone,
                      DATE_FORMAT.DEFAULT_FULL_MONTH
                    )
                  : ""
              }
            />,
            <AdminMembersReportStandardProfileFieldsTableRow
              key={"last_active"}
              fieldName={"Last active"}
              response={
                organizationMembership.lastActivityAt
                  ? formatInTimeZone(
                      organizationMembership.lastActivityAt,
                      authUserTimezone,
                      DATE_FORMAT.DEFAULT_FULL_MONTH
                    )
                  : ""
              }
            />,
          ]}
        </TableBody>
      </Table>
    </DiscoSection>
  )
}

const useStyles = makeUseStyles((theme) => ({
  section: {
    display: "grid",
    overflowX: "auto",
  },
  root: {
    display: "table",
    overflowY: "auto",
    border: "none",
    /** table row spacing */
    borderCollapse: "separate",
    borderSpacing: `0 ${theme.spacing(1)}px`,

    "& td, thead, tr, tfoot": {
      borderStyle: "hidden",
    },
    "& td, th": {
      padding: theme.spacing(1),
    },
    "& th": {
      // Don't let headers stick to the left side and slide over each other
      left: "unset",
    },
  },
  headerRow: {
    ...theme.typography["body-xs"],
    ...theme.typography.modifiers.fontWeight[600],
    "& .MuiTableCell-head": {
      "&:nth-child(1)": {
        [theme.breakpoints.down("xs")]: {
          paddingLeft: 0,
        },
      },
    },
    "& th": {
      /** header border radius */
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.groovy.onDark[500]
          : theme.palette.groovy.neutral[100],
      "&:first-child": {
        borderTopLeftRadius: theme.measure.borderRadius.default,
        borderBottomLeftRadius: theme.measure.borderRadius.default,
      },
      "&:last-child": {
        borderTopRightRadius: theme.measure.borderRadius.default,
        borderBottomRightRadius: theme.measure.borderRadius.default,
      },
    },
  },
  memberGroupsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
}))

function AdminMembersReportsStandardProfileFieldsTableSkeleton() {
  return (
    <DiscoTableSkeletonWithHeader
      header={headers}
      rows={6}
      row={<AdminMembersReportStandardProfileFieldsTableRowSkeleton />}
    />
  )
}

export default Relay.withSkeleton({
  component: AdminMembersReportsStandardProfileFieldsTable,
  skeleton: AdminMembersReportsStandardProfileFieldsTableSkeleton,
})
